import { JobFormErrors, JobPostingAction } from "./job.reducer";
import { Job, JobSkill } from "../model/JobModel";

interface ConfirmationState {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  type: "danger" | "warning" | "info";
  loading: boolean;
}

export type JobManagementAction =
  | { type: "SET_FORM_DATA"; payload: Partial<Job> }
  | { type: "UPDATE_FORM_FIELD"; payload: { field: keyof Job; value: any } }
  | { type: "ADD_SKILL"; payload: JobSkill }
  | { type: "DELETE_SKILL"; payload: string }
  | {
      type: "UPDATE_SKILL";
      payload: { skill_id: string; updates: Partial<JobSkill> };
    }
  | { type: "SET_NEW_SKILL"; payload: JobSkill }
  | { type: "SET_SHOW_MODAL"; payload: boolean }
  | {
      type: "SET_TOAST";
      payload: {
        isVisible: boolean;
        message: string;
        type: "success" | "error" | "warning" | "info";
      };
    }
  | { type: "HIDE_TOAST" }
  | { type: "SET_JOBS"; payload: Job[] }
  | { type: "SET_JOBS_MOCK_DATA"; payload: Job[] }
  | { type: "SET_ERROR"; payload: string | null }
  | { type: "SET_EDITING_JOB"; payload: Job | null }
  | { type: "SET_TITLE"; payload: string }
  | { type: "SET_DEPARTMENT"; payload: string }
  | { type: "SET_LOCATION"; payload: string }
  | { type: "SET_SEARCH_TERM"; payload: string }
  | {
      type: "SET_ACTIVE_TAB";
      payload: "jobs" | "applications" | "pendingApprovals";
    }
  | { type: "SET_CONFIRMATION"; payload: ConfirmationState }
  | { type: "HIDE_CONFIRMATION" }
  | { type: "SET_FORM_ERRORS"; payload: JobFormErrors }
  | { type: "CLEAR_FORM_ERROR"; payload: keyof JobFormErrors }
  | { type: "RESET_FORM" }
  | { type: "PREFILL_FORM"; payload: any }
  | { type: "SET_USERS"; payload: any[] }
  | { type: "SET_USERS"; payload: any[] }
  | { type: "SET_APPROVAL_FLOWS"; payload: any[] }
  | {
      type: "SHOW_APPROVAL_POPUP";
      payload: { jobId: string; flowId: string };
    }
  | { type: "HIDE_APPROVAL_POPUP" }
  | { type: "SET_SELECTED_APPROVAL_FLOW"; payload: string }
  | { type: "SET_CREATE_FROM_DRAFT"; payload: boolean };

export const setSearchTerm = (payload: string): JobPostingAction => ({
  type: "SET_SEARCH_TERM",
  payload,
});

export const setStatusFilter = (payload: string): JobPostingAction => ({
  type: "SET_STATUS_FILTER",
  payload,
});

export const setCurrentPage = (payload: number): JobPostingAction => ({
  type: "SET_CURRENT_PAGE",
  payload,
});
export const setApplicationCurrentPage = (
  payload: number,
): JobPostingAction => ({
  type: "SET_APPLICATION_CURRENT_PAGE",
  payload,
});

export const setOpenMenu = (payload: string | null) => ({
  type: "SET_OPEN_MENU" as const,
  payload,
});

export const setViewMode = (payload: "list" | "grid"): JobPostingAction => ({
  type: "SET_VIEW_MODE",
  payload,
});

export const setVisibility = (payload: string): JobPostingAction => ({
  type: "SET_VISIBILITY",
  payload,
});
export const setJobDetails = (payload: any): JobPostingAction => ({
  type: "SET_JOB_DETAILS",
  payload,
});
export const setJobApplications = (payload: any): JobPostingAction => ({
  type: "SET_JOB_APPLICATIONS",
  payload,
});
export const setDepartment = (payload: string): JobPostingAction => ({
  type: "SET_DEPARTMENT",
  payload,
});
export const setTitle = (payload: string): JobPostingAction => ({
  type: "SET_TITLE",
  payload,
});
export const setLocation = (payload: string): JobPostingAction => ({
  type: "SET_LOCATION",
  payload,
});
export const setHiringManagerId = (payload: string): JobPostingAction => ({
  type: "SET_HIRING_MANAGER_ID",
  payload,
});

export const setRecruiterId = (payload: string): JobPostingAction => ({
  type: "SET_RECRUITER_ID",
  payload,
});

export const setLimit = (payload: number): JobPostingAction => ({
  type: "SET_LIMIT",
  payload,
});

export const setApplicationLimit = (payload: number): JobPostingAction => ({
  type: "SET_APPLICATION_LIMIT",
  payload,
});

export const setOffset = (payload: number): JobPostingAction => ({
  type: "SET_OFFSET",
  payload,
});
export const setApplicationOpen = (payload: boolean): JobPostingAction => ({
  type: "OPEN_APPLICATION_VIEW",
  payload,
});

export const setLoading = (payload: boolean): JobPostingAction => ({
  type: "SET_LOADING",
  payload,
});

export const setError = (payload: string | null): JobPostingAction => ({
  type: "SET_ERROR",
  payload,
});

export const setJobs = (payload: any[]): JobPostingAction => ({
  type: "SET_JOBS",
  payload,
});
export const setFormErrors = (payload: JobFormErrors) => ({
  type: "SET_FORM_ERRORS" as const,
  payload,
});
export const resetForm = () => ({
  type: "RESET_FORM" as const,
});

export const clearFormError = (field: keyof JobFormErrors) => ({
  type: "CLEAR_FORM_ERROR",
  payload: field,
});

export const setTotal = (payload: number): JobPostingAction => ({
  type: "SET_TOTAL",
  payload,
});
export const setApplicationTotal = (payload: number): JobPostingAction => ({
  type: "SET_APPLICATION_TOTAL",
  payload,
});
export const setContactPopup = (application: any): JobPostingAction => ({
  type: "SET_CONTACT_POPUP",
  payload: application,
});
export const setUsers = (payload: any[]): JobPostingAction => ({
  type: "SET_USERS",
  payload,
});
export const setJOBToast = (payload: {
  isVisible: boolean;
  message: string;
  type: "success" | "error" | "warning" | "info";
}): JobPostingAction => ({
  type: "SET_JOB_TOAST",
  payload,
});

export const hideJOBToast = (): JobPostingAction => ({
  type: "HIDE_JOB_TOAST",
});
// Job Management Actions
export const setFormData = (payload: Partial<Job>): JobManagementAction => ({
  type: "SET_FORM_DATA",
  payload,
});

export const updateFormField = (
  field: keyof Job,
  value: any,
): JobManagementAction => ({
  type: "UPDATE_FORM_FIELD",
  payload: { field, value },
});

export const addSkill = (payload: JobSkill): JobManagementAction => ({
  type: "ADD_SKILL",
  payload,
});

export const deleteSkill = (payload: string): JobManagementAction => ({
  type: "DELETE_SKILL",
  payload,
});
export const updateSkill = (skill_id: string, updates: Partial<JobSkill>) => ({
  type: "UPDATE_SKILL" as const,
  payload: { skill_id, updates },
});
export const setNewSkill = (payload: JobSkill): JobManagementAction => ({
  type: "SET_NEW_SKILL",
  payload,
});

export const setShowModal = (payload: boolean): JobManagementAction => ({
  type: "SET_SHOW_MODAL",
  payload,
});

export const setToast = (payload: {
  isVisible: boolean;
  message: string;
  type: "success" | "error" | "warning" | "info";
}): JobManagementAction => ({
  type: "SET_TOAST",
  payload,
});

export const hideToast = (): JobManagementAction => ({
  type: "HIDE_TOAST",
});

// export const setJobs = (payload: Job[]): JobManagementAction => ({
//   type: 'SET_JOBS',
//   payload,
// });

export const setJobsMockData = (payload: Job[]): JobManagementAction => ({
  type: "SET_JOBS_MOCK_DATA",
  payload,
});

export const setEditingJob = (payload: Job | null): JobManagementAction => ({
  type: "SET_EDITING_JOB",
  payload,
});
export const setPrefillForm = (payload: Job | null): JobManagementAction => ({
  type: "PREFILL_FORM",
  payload,
});

export const setSearchTermManagement = (
  payload: string,
): JobManagementAction => ({
  type: "SET_SEARCH_TERM",
  payload,
});

export const setActiveTab = (
  payload: "jobs" | "applications" | "pendingApprovals",
): JobManagementAction => ({
  type: "SET_ACTIVE_TAB",
  payload,
});

export const setConfirmation = (
  payload: ConfirmationState,
): JobManagementAction => ({
  type: "SET_CONFIRMATION",
  payload,
});

export const hideConfirmation = (): JobManagementAction => ({
  type: "HIDE_CONFIRMATION",
});

export const setUsersManagement = (payload: any[]): JobManagementAction => ({
  type: "SET_USERS",
  payload,
});
export const setApprovalFlows = (payload: any[]): JobManagementAction => ({
  type: "SET_APPROVAL_FLOWS",
  payload,
});

// export const showApprovalPopup = (payload: string): JobManagementAction => ({
//   type: "SHOW_APPROVAL_POPUP",
//   payload,
// });

export const hideApprovalPopup = (): JobManagementAction => ({
  type: "HIDE_APPROVAL_POPUP",
});

export const setSelectedApprovalFlow = (
  payload: string,
): JobManagementAction => ({
  type: "SET_SELECTED_APPROVAL_FLOW",
  payload,
});
